/**
 * lib/ninetailed/buildNinetailedOptions.ts
 *
 * Pure factory that turns the public Ninetailed env (Client ID + environment,
 * from `NEXT_PUBLIC_*` — see lib/ninetailed/env.ts) plus preview state into the
 * option set the `NinetailedProvider` needs. Kept free of React so it is
 * unit-testable under the repo's `%20`-folder constraint (pure functions only).
 *
 * Plugin policy:
 *  - insights: always included (event/analytics ingestion).
 *  - preview:  included ONLY in preview/draft mode (editor widget). Its
 *              Experiences/Audiences panels are populated from the catalog arrays
 *              the provider fetches and passes in (`previewExperiences`/
 *              `previewAudiences`); empty arrays => empty panels. The optional
 *              `onOpenExperienceEditor`/`onOpenAudienceEditor` callbacks power the
 *              widget's "Go to editor" deep-links — when omitted the widget shows a
 *              "not fully set up to support Go to editor" warning, so the provider
 *              always supplies them in preview mode.
 *  - privacy:  always included. Configured STRICT in normal (non-preview) mode —
 *              blocks ALL events until the visitor grants consent (see below).
 *              Consent is bridged from OneTrust to the SDK at runtime via
 *              `window.ninetailed.consent()`. In PREVIEW mode it is configured
 *              PERMISSIVE instead: the Ninetailed preview widget can only force a
 *              variant once the SDK has resolved a visitor profile, and the SDK
 *              only fetches that profile as a side effect of sending events — which
 *              the strict gate blocks pre-consent. Without a profile the core SDK
 *              treats every preview override as a no-op (it returns a pass-through
 *              selection middleware when `profile === null`), so the widget's
 *              "Baseline/Variant" picker changes the selection but the page keeps
 *              rendering the baseline. Preview is an editor-only context (Contentful
 *              draft preview or dev-forced) — real visitors are never in preview
 *              mode — so relaxing the gate there has no consent impact on
 *              production traffic while making variant forcing actually render.
 */

import type { NinetailedPlugin } from '@ninetailed/experience.js-plugin-analytics';
import { FEATURES } from '@ninetailed/experience.js-shared';
import type { ExperienceConfiguration } from '@ninetailed/experience.js-shared';
import type { ExposedAudienceDefinition } from '@ninetailed/experience.js-preview-bridge';
import { NinetailedInsightsPlugin } from '@ninetailed/experience.js-plugin-insights';
import { NinetailedPreviewPlugin } from '@ninetailed/experience.js-plugin-preview';
import { NinetailedPrivacyPlugin } from '@ninetailed/experience.js-plugin-privacy';

/** SDK request timeout (ms). Also the fallback-to-baseline threshold. */
export const NINETAILED_REQUEST_TIMEOUT = 5000;

/**
 * How long (ms) a personalized component must be continuously in-viewport before
 * the SDK records a native component-view (impression) for CTR. Matches the SDK's
 * own default (2000ms); surfaced here as a named constant so the dwell threshold
 * is tunable in one place and passed explicitly to `<NinetailedProvider>`.
 *
 * KNOWN LIMITATION (starter scope): the SDK's view observer is a geometric
 * IntersectionObserver that ignores `opacity`/`pointer-events`. The main-banner
 * carousel mounts all slides at `absolute inset-0` and toggles them via
 * `opacity-0`, so inactive slides are still "intersecting" and their impressions
 * are counted — inflating the CTR denominator on multi-slide carousels. Scoping
 * impressions to the active slide is tracked as a follow-up task.
 */
export const NINETAILED_COMPONENT_VIEW_TRACKING_THRESHOLD = 2000;

/**
 * Strict pre-consent privacy config for the Ninetailed SDK. `allowedEvents: []`
 * blocks EVERY event type (page, identify, track, component/click/hover) until
 * the visitor grants consent. This is deliberately stricter than the plugin's
 * default (which allows `page`): because the SDK only fetches the visitor profile
 * — and only then writes its localStorage caches (`__nt_anonymous_id__`,
 * `__nt_profile__`, `__nt_experiences__`) — as a side effect of flushing queued
 * events, blocking all events means NOTHING is sent and NOTHING is persisted
 * before consent. Trade-off: personalization does not resolve pre-consent, so the
 * baseline renders until consent is granted (matches our GA4 deny-by-default).
 *
 * The second `NinetailedPrivacyPlugin` argument (accepted-consent config) is left
 * as the plugin default, which re-enables all events/traits/features once consent
 * is granted via `window.ninetailed.consent(true)`.
 */
const NINETAILED_PRE_CONSENT_PRIVACY_CONFIG = {
  allowedEvents: [],
};

/** Privacy plugin config type, derived from the plugin's constructor. */
type PrivacyPluginConfig = NonNullable<
  ConstructorParameters<typeof NinetailedPrivacyPlugin>[0]
>;

/**
 * Permissive privacy config used ONLY in preview mode. It mirrors the plugin's own
 * "consent accepted" defaults (all event types, all traits, profile merging, all
 * features), so the SDK behaves as if consent were already granted: it sends the
 * `page` event, fetches the visitor profile, and evaluates audiences/experiences.
 * That resolved profile is a hard prerequisite for the preview widget's variant
 * forcing — the core SDK ignores every preview override while `profile === null`.
 * Preview is editor-only, so this never relaxes the gate for real visitors (they
 * always get `NINETAILED_PRE_CONSENT_PRIVACY_CONFIG`).
 */
const NINETAILED_PREVIEW_PRIVACY_CONFIG: PrivacyPluginConfig = {
  allowedEvents: [
    'page',
    'track',
    'identify',
    'component',
    'component_click',
    'component_hover',
  ],
  allowedPageEventProperties: ['*'],
  allowedTrackEventProperties: ['*'],
  allowedTrackEvents: ['*'],
  allowedTraits: ['*'],
  blockProfileMerging: false,
  enabledFeatures: Object.values(FEATURES),
};

export interface NinetailedEnv {
  /** Public Ninetailed Client ID (from `NEXT_PUBLIC_NINETAILED_CLIENT_ID`). */
  clientId?: string;
  /** Ninetailed environment slug (from `NEXT_PUBLIC_NINETAILED_ENVIRONMENT`). */
  environment?: string;
}

export interface NinetailedOptions {
  clientId: string;
  environment?: string;
  requestTimeout: number;
  /** Dwell time (ms) before a component-view impression is recorded. */
  componentViewTrackingThreshold: number;
  plugins: NinetailedPlugin[];
}

export interface BuildNinetailedOptionsParams {
  previewMode?: boolean;
  /**
   * Full catalog of experiences to list in the preview widget's Experiences
   * panel. Ignored outside preview mode. The plugin captures this at construction,
   * so it must be populated before the plugin is built (see the provider).
   */
  previewExperiences?: ExperienceConfiguration[];
  /**
   * Full catalog of audiences to list in the preview widget's Audiences panel.
   * Ignored outside preview mode.
   */
  previewAudiences?: ExposedAudienceDefinition[];
  /**
   * "Go to editor" handler for an experience row in the preview widget. Receives
   * the `ExperienceConfiguration` (its `id` is the `nt_experience_id`). Providing
   * it — even as a no-op — clears the widget's "not fully set up" warning and
   * renders the button. Ignored outside preview mode.
   */
  onOpenExperienceEditor?: (experience: ExperienceConfiguration) => void;
  /**
   * "Go to editor" handler for an audience row in the preview widget. Receives the
   * `ExposedAudienceDefinition` (its `id` is the `nt_audience_id`). Ignored outside
   * preview mode.
   */
  onOpenAudienceEditor?: (audience: ExposedAudienceDefinition) => void;
}

export function buildNinetailedOptions(
  env: NinetailedEnv | undefined,
  {
    previewMode = false,
    previewExperiences = [],
    previewAudiences = [],
    onOpenExperienceEditor,
    onOpenAudienceEditor,
  }: BuildNinetailedOptionsParams = {},
): NinetailedOptions {
  const clientId = env?.clientId ?? '';
  const environment = env?.environment || undefined;

  // Privacy plugin first so it intercepts and aborts disallowed events before any
  // other plugin (e.g. insights) can process them. STRICT pre-consent for real
  // visitors; PERMISSIVE in preview so the SDK resolves a profile and the preview
  // widget's variant forcing can actually render (see the module header).
  const plugins: NinetailedPlugin[] = [
    new NinetailedPrivacyPlugin(
      previewMode
        ? NINETAILED_PREVIEW_PRIVACY_CONFIG
        : NINETAILED_PRE_CONSENT_PRIVACY_CONFIG,
    ),
    new NinetailedInsightsPlugin(),
  ];

  if (previewMode) {
    // The widget's Experiences/Audiences panels are rendered from these arrays;
    // empty arrays => empty panels. The provider fetches the catalog and passes
    // it in, so the panels list every experience/audience in the space.
    plugins.push(
      new NinetailedPreviewPlugin({
        experiences: previewExperiences,
        audiences: previewAudiences,
        onOpenExperienceEditor,
        onOpenAudienceEditor,
      }),
    );
  }

  return {
    clientId,
    environment,
    requestTimeout: NINETAILED_REQUEST_TIMEOUT,
    componentViewTrackingThreshold: NINETAILED_COMPONENT_VIEW_TRACKING_THRESHOLD,
    plugins,
  };
}
